/**
 * Import Routes Constants
 * Single source of truth for all import-related API endpoints
 * Used by both frontend and backend for consistency
 */

/**
 * Import routes - Complete set
 * Based on backend implementation but centralized here
 */
export const IMPORT_ROUTES = {
  BASE: '/api/imports',
  CONFIG: '/api/imports/config',
  CSV_FORMATS: '/api/imports/csv-formats',
  CUSTOM_IMPORT: '/api/imports/custom-import',

  // Legacy aliases for backward compatibility
  UPDATE_SOURCE: (id: string | number) => `/api/imports/sources/${id}`,
  DELETE_SOURCE: (id: string | number) => `/api/imports/sources/${id}`,

  // Import Jobs
  JOBS: {
    BASE: '/api/imports/jobs',
    LIST: '/api/imports/jobs',
    CREATE: '/api/imports/jobs',
    GET_BY_ID: (id: string | number) => `/api/imports/jobs/${id}`,
    UPDATE: (id: string | number) => `/api/imports/jobs/${id}`,
    DELETE: (id: string | number) => `/api/imports/jobs/${id}`,
    CANCEL: (id: string | number) => `/api/imports/jobs/${id}/cancel`,
    RETRY: (id: string | number) => `/api/imports/jobs/${id}/retry`,
    STATUS: (id: string | number) => `/api/imports/jobs/${id}/status`,
    LOGS: (id: string | number) => `/api/imports/jobs/${id}/logs`,
    DOWNLOAD_RESULTS: (id: string | number) => `/api/imports/jobs/${id}/download`,
    HEALTH: '/api/imports/jobs/health',
  },

  // Imported Transactions (pending approval)
  TRANSACTIONS: {
    BASE: '/api/imports/transactions',
    LIST: '/api/imports/transactions',
    GET_BY_ID: (id: string | number) => `/api/imports/transactions/${id}`,
    GET_BY_JOB: (jobId: string | number) => `/api/imports/jobs/${jobId}/transactions`,
    BULK_APPROVE: (jobId: string | number) =>
      `/api/imports/jobs/${jobId}/transactions/bulk-approve`,
    BULK_REJECT: (jobId: string | number) => `/api/imports/jobs/${jobId}/transactions/bulk-reject`,
    STATS: (jobId: string | number) => `/api/imports/jobs/${jobId}/stats`,
  },

  // Import Sources
  SOURCES: {
    BASE: '/api/imports/sources',
    LIST: '/api/imports/sources',
    CREATE: '/api/imports/sources',
    API: '/api/imports/sources/api',
    CSV: '/api/imports/sources/csv',
    BLOCKCHAIN: '/api/imports/sources/blockchain',
    GET_BY_ID: (id: string | number) => `/api/imports/sources/${id}`,
    UPDATE: (id: string | number) => `/api/imports/sources/${id}`,
    DELETE: (id: string | number) => `/api/imports/sources/${id}`,
    SYNC: (id: string | number) => `/api/imports/sources/${id}/sync`,
    PAUSE: (id: string | number) => `/api/imports/sources/${id}/pause`,
    // Gemelo de PAUSE. Existía en el backend (`api/routes/source-routes.ts`)
    // desde antes que esta constante: sin ella, `/import` sabía pausar y no
    // sabía deshacerlo.
    RESUME: (id: string | number) => `/api/imports/sources/${id}/resume`,
    SYNC_BULK: '/api/imports/sources/sync-bulk',
    PAUSE_BULK: '/api/imports/sources/pause-bulk',
    SYNC_ALL: '/api/imports/sources/sync-all',
    PAUSE_ALL: '/api/imports/sources/pause-all',
    SYNC_STATUS: (id: string | number) => `/api/imports/sources/${id}/status`,
    TEST_CONNECTION: (id: string | number) => `/api/imports/sources/${id}/test`,
    HEALTH: '/api/imports/sources/health',
  },

  // Import Providers
  PROVIDERS: {
    BASE: '/api/imports/providers',
    LIST: '/api/imports/providers',
    GET_BY_ID: (id: string | number) => `/api/imports/providers/${id}`,
    VALIDATE_API: '/api/imports/providers/validate-api',
    HEALTH: '/api/imports/providers/health',
  },

  // Import Templates
  TEMPLATES: {
    BASE: '/api/imports/templates',
    LIST: '/api/imports/templates',
    CREATE: '/api/imports/templates',
    GET_BY_ID: (id: string | number) => `/api/imports/templates/${id}`,
    UPDATE: (id: string | number) => `/api/imports/templates/${id}`,
    DELETE: (id: string | number) => `/api/imports/templates/${id}`,
    DUPLICATE: (id: string | number) => `/api/imports/templates/${id}/duplicate`,
    HEALTH: '/api/imports/templates/health',
  },

  // File Upload
  UPLOAD: {
    BASE: '/api/imports/upload',
    FILE: '/api/imports/upload/file',
    VALIDATE: '/api/imports/upload/validate',
    PREVIEW: '/api/imports/upload/preview',
    PROCESS_ASYNC: '/api/imports/upload/process-async',
    HEALTH: '/api/imports/upload/health',
  },

  // CSV Processing
  CSV: {
    BASE: '/api/imports/csv',
    PROCESS_FILE: '/api/imports/csv/process-file',
    PROCESS_SIMPLE: '/api/imports/csv/process-simple',
    PREVIEW_WITH_TEMPLATE: '/api/imports/csv/preview-with-template',
    PREVIEW_WITH_INLINE_CONFIG: '/api/imports/csv/preview-with-inline-config',
  },

  // Direct API connectors
  API: {
    BASE: '/api/imports/api',
    TEST_CREDENTIALS: '/api/imports/api/test-credentials',
  },

  // Sync history
  SYNC: {
    BASE: '/api/imports/sync',
    HISTORY: '/api/imports/sync/history',
    HISTORY_DETAIL: (logId: string) => `/api/imports/sync/history/${logId}`,
    HISTORY_RETRY: (logId: string) => `/api/imports/sync/history/${logId}/retry`,
  },

  // Needs-attention resolver
  NEEDS_ATTENTION: {
    LIST: '/api/imports/needs-attention',
    RESOLVE_ONE: (txId: string) => `/api/imports/needs-attention/${txId}/resolve`,
    BULK_RESOLVE: '/api/imports/needs-attention/bulk-resolve',
    REPROCESS_UNKNOWN: '/api/imports/unknown-rows/reprocess',
  },

  // Data Sources
  DATA_SOURCES: {
    BASE: '/api/imports/data-sources',
    LIST: '/api/imports/data-sources',
    CREATE: '/api/imports/data-sources',
    VALIDATE: '/api/imports/data-sources/validate',
  },

  // Mappings
  MAPPINGS: {
    BASE: '/api/imports/mappings',
    VALIDATE: '/api/imports/mappings/validate',
  },

  // Queue Status
  QUEUE: {
    BASE: '/api/imports/queue',
    STATUS: '/api/imports/queue/status',
  },

  HEALTH: '/api/imports/health',
} as const

/**
 * Import route path types for type safety
 */
export type ImportRoutePath = (typeof IMPORT_ROUTES)[keyof typeof IMPORT_ROUTES]
export type ImportRouteKey = keyof typeof IMPORT_ROUTES
export type ImportJobRoutePath = (typeof IMPORT_ROUTES.JOBS)[keyof typeof IMPORT_ROUTES.JOBS]
export type ImportTransactionRoutePath =
  (typeof IMPORT_ROUTES.TRANSACTIONS)[keyof typeof IMPORT_ROUTES.TRANSACTIONS]
export type ImportSourceRoutePath =
  (typeof IMPORT_ROUTES.SOURCES)[keyof typeof IMPORT_ROUTES.SOURCES]
export type ImportProviderRoutePath =
  (typeof IMPORT_ROUTES.PROVIDERS)[keyof typeof IMPORT_ROUTES.PROVIDERS]
export type ImportTemplateRoutePath =
  (typeof IMPORT_ROUTES.TEMPLATES)[keyof typeof IMPORT_ROUTES.TEMPLATES]
export type ImportUploadRoutePath = (typeof IMPORT_ROUTES.UPLOAD)[keyof typeof IMPORT_ROUTES.UPLOAD]

/**
 * Route utilities for import endpoints
 */
export const IMPORT_ROUTE_UTILS = {
  /**
   * Get route without /api prefix (useful for backend route registration)
   */
  getWithoutApiPrefix: (route: string): string => {
    return route.replace('/api', '')
  },

  /**
   * Validate that a string matches an import route pattern
   */
  isImportRoute: (path: string): boolean => {
    return path.startsWith('/api/imports/')
  },

  /**
   * Get all public import routes (no authentication required)
   */
  getPublicRoutes: (): string[] => [
    IMPORT_ROUTES.HEALTH,
    IMPORT_ROUTES.JOBS.HEALTH,
    IMPORT_ROUTES.SOURCES.HEALTH,
    IMPORT_ROUTES.PROVIDERS.HEALTH,
    IMPORT_ROUTES.UPLOAD.HEALTH,
    IMPORT_ROUTES.PROVIDERS.LIST, // Public list of supported providers
  ],

  /**
   * Get all protected import routes (authentication required)
   */
  getProtectedRoutes: (): string[] => [
    IMPORT_ROUTES.JOBS.LIST,
    IMPORT_ROUTES.JOBS.CREATE,
    IMPORT_ROUTES.SOURCES.LIST,
    IMPORT_ROUTES.SOURCES.CREATE,
    IMPORT_ROUTES.PROVIDERS.VALIDATE_API,
    IMPORT_ROUTES.TEMPLATES.LIST,
    IMPORT_ROUTES.TEMPLATES.CREATE,
    IMPORT_ROUTES.UPLOAD.FILE,
    IMPORT_ROUTES.UPLOAD.VALIDATE,
    IMPORT_ROUTES.UPLOAD.PREVIEW,
  ],

  /**
   * Build parameterized route with ID
   */
  buildRouteWithId: (template: (id: string | number) => string, id: string | number): string => {
    return template(id)
  },
} as const

/**
 * Route metadata for documentation and validation
 */
export const IMPORT_ROUTE_METADATA = {
  // Main import routes
  [IMPORT_ROUTES.HEALTH]: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'Import service health check',
  },

  // Import Jobs
  [`${IMPORT_ROUTES.JOBS.LIST}_LIST`]: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'List import jobs with filtering',
  },
  [`${IMPORT_ROUTES.JOBS.CREATE}_CREATE`]: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Create a new import job',
  },
  [IMPORT_ROUTES.JOBS.HEALTH]: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'Import jobs service health check',
  },

  // Import Sources
  [`${IMPORT_ROUTES.SOURCES.LIST}_LIST`]: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'List import sources',
  },
  [`${IMPORT_ROUTES.SOURCES.CREATE}_CREATE`]: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Create a new import source',
  },
  [IMPORT_ROUTES.SOURCES.HEALTH]: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'Import sources service health check',
  },

  // Import Providers
  [IMPORT_ROUTES.PROVIDERS.LIST]: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'List supported import providers',
  },
  [IMPORT_ROUTES.PROVIDERS.VALIDATE_API]: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Validate API credentials for provider',
  },
  [IMPORT_ROUTES.PROVIDERS.HEALTH]: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'Import providers service health check',
  },

  // Import Templates
  [`${IMPORT_ROUTES.TEMPLATES.LIST}_LIST`]: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'List import templates',
  },
  [`${IMPORT_ROUTES.TEMPLATES.CREATE}_CREATE`]: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Create a new import template',
  },
  [IMPORT_ROUTES.TEMPLATES.HEALTH]: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'Import templates service health check',
  },

  // File Upload
  [IMPORT_ROUTES.UPLOAD.FILE]: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Upload file for import',
  },
  [IMPORT_ROUTES.UPLOAD.VALIDATE]: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Validate uploaded file format',
  },
  [IMPORT_ROUTES.UPLOAD.PREVIEW]: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Preview import data before processing',
  },
  [IMPORT_ROUTES.UPLOAD.HEALTH]: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'File upload service health check',
  },
} as const

/**
 * Dynamic route metadata for parameterized routes
 */
export const IMPORT_DYNAMIC_ROUTE_METADATA = {
  // Import Jobs dynamic routes
  JOB_GET_BY_ID: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'Get import job by ID',
    pathTemplate: '/api/imports/jobs/:id',
  },
  JOB_UPDATE: {
    method: 'PUT' as const,
    requiresAuth: true,
    description: 'Update import job by ID',
    pathTemplate: '/api/imports/jobs/:id',
  },
  JOB_DELETE: {
    method: 'DELETE' as const,
    requiresAuth: true,
    description: 'Delete import job by ID',
    pathTemplate: '/api/imports/jobs/:id',
  },
  JOB_CANCEL: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Cancel running import job',
    pathTemplate: '/api/imports/jobs/:id/cancel',
  },
  JOB_RETRY: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Retry failed import job',
    pathTemplate: '/api/imports/jobs/:id/retry',
  },
  JOB_STATUS: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'Get import job status',
    pathTemplate: '/api/imports/jobs/:id/status',
  },
  JOB_LOGS: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'Get import job logs',
    pathTemplate: '/api/imports/jobs/:id/logs',
  },
  JOB_DOWNLOAD_RESULTS: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'Download import job results',
    pathTemplate: '/api/imports/jobs/:id/download',
  },

  // Import Sources dynamic routes
  SOURCE_GET_BY_ID: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'Get import source by ID',
    pathTemplate: '/api/imports/sources/:id',
  },
  SOURCE_UPDATE: {
    method: 'PUT' as const,
    requiresAuth: true,
    description: 'Update import source by ID',
    pathTemplate: '/api/imports/sources/:id',
  },
  SOURCE_DELETE: {
    method: 'DELETE' as const,
    requiresAuth: true,
    description: 'Delete import source by ID',
    pathTemplate: '/api/imports/sources/:id',
  },
  SOURCE_SYNC: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Sync import source by ID',
    pathTemplate: '/api/imports/sources/:id/sync',
  },
  SOURCE_SYNC_STATUS: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'Get import source sync status',
    pathTemplate: '/api/imports/sources/:id/status',
  },
  SOURCE_TEST_CONNECTION: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Test import source connection',
    pathTemplate: '/api/imports/sources/:id/test',
  },

  // Import Providers dynamic routes
  PROVIDER_GET_BY_ID: {
    method: 'GET' as const,
    requiresAuth: false,
    description: 'Get import provider by ID',
    pathTemplate: '/api/imports/providers/:id',
  },

  // Import Templates dynamic routes
  TEMPLATE_GET_BY_ID: {
    method: 'GET' as const,
    requiresAuth: true,
    description: 'Get import template by ID',
    pathTemplate: '/api/imports/templates/:id',
  },
  TEMPLATE_UPDATE: {
    method: 'PUT' as const,
    requiresAuth: true,
    description: 'Update import template by ID',
    pathTemplate: '/api/imports/templates/:id',
  },
  TEMPLATE_DELETE: {
    method: 'DELETE' as const,
    requiresAuth: true,
    description: 'Delete import template by ID',
    pathTemplate: '/api/imports/templates/:id',
  },
  TEMPLATE_DUPLICATE: {
    method: 'POST' as const,
    requiresAuth: true,
    description: 'Duplicate import template',
    pathTemplate: '/api/imports/templates/:id/duplicate',
  },
} as const

/**
 * Type for route metadata
 */
export type ImportRouteMetadata = (typeof IMPORT_ROUTE_METADATA)[keyof typeof IMPORT_ROUTE_METADATA]
export type ImportDynamicRouteMetadata =
  (typeof IMPORT_DYNAMIC_ROUTE_METADATA)[keyof typeof IMPORT_DYNAMIC_ROUTE_METADATA]
